import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, CreditCard, Clock, Calendar, Shield, Download, Share2, Users, Zap } from 'lucide-react';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';

const TimePassPricing = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  
const periodOptions = [
  { days: '30', price: 38, label: '30-day pass' },
  { days: '60', price: 76, label: '60-day pass' },
  { days: '90', price: 114, label: '90-day pass' },
  { days: '180', price: 228, label: '180-day pass' },
  { days: '360', price: 456, label: '360-day pass' }
];
  
  const currentOption = periodOptions.find(option => option.days === selectedPeriod);
  
  const features = [
    'Access to all AI design features',
    'Unlimited project creations',
    'Create interactive designs, prototypes, and flows',
    'Share your designs using secure links',
    'Export HTML and CSS code',
    'No auto-renewals or subscriptions'
  ];

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-4">Pay As You Go</h2>
        <p className="text-gray-600 mb-6">
          Simple, flexible access - pay only when you need it, with no auto-renewals.
        </p>
        
        {/* Price Display */}
        <div className="mb-6 bg-gray-50 p-6 rounded-lg">
          <div className="flex items-end">
            <span className="text-4xl font-bold text-gray-900">${currentOption.price}</span>
            <span className="text-gray-600 ml-2">for {currentOption.days} days</span>
          </div>
          <p className="text-sm text-gray-500 mt-2">One-time payment, no recurring charges</p>
        </div>
        
        {/* Duration Selection */}
        <div className="mb-6">
          <label htmlFor="period-select" className="block text-sm font-medium text-gray-700 mb-2">
            Select Access Duration
          </label>
          <select
            id="period-select"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="block w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
          >
            {periodOptions.map((option) => (
              <option key={option.days} value={option.days}>
                {option.days}-day pass (${option.price})
              </option>
            ))}
          </select>
        </div>
        
        {/* Features List */}
        <div className="mb-6">
          <h3 className="font-semibold text-gray-800 mb-3">What's included:</h3>
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      
        
        {/* Important Notes */}
        <div className="bg-blue-50 rounded-md p-4 mb-6">
          <h4 className="font-semibold text-blue-900 mb-2">Simple & Flexible:</h4>
          <ul className="text-sm text-blue-800 space-y-1 list-inside">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>No auto-renewals - passes expire automatically without charges</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Complete flexibility - renew only when you need to</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Buy a new pass anytime after expiration, on your schedule</span>
            </li>
          </ul>
        </div>
        
        <Link
          to="/signup"
          className="w-full bg-black text-white py-3 px-4 rounded-md hover:bg-gray-800 transition-colors duration-200 font-medium inline-block text-center"
        >
          Get Started Free
        </Link>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon: Icon, title, description }) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="flex items-center mb-4">
        <div className="bg-orange-100 p-3 rounded-lg mr-4">
          <Icon className="h-6 w-6 text-orange-500" />
        </div>
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

const Pricing = () => {
  return (
    <div className="font-sans antialiased text-gray-900">
      {/* Navbar with pricing as the active page */}
      <MainNavbar activePage="pricing" />
      
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Simple, Transparent Pricing</h1>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Pay for what you need, when you need it. No subscriptions, no auto-renewals.
          </p>
        </div>
      </section>
      
      {/* Pricing Cards */}
      <section className="py-16 -mt-10">
        <div className="max-w-2xl mx-auto px-4">
          <TimePassPricing />
        </div>
      </section>
      
      {/* Why Choose Our Pricing Model */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose Our Pricing Model</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={CreditCard}
              title="Simple Billing"
              description="No recurring charges or surprise fees. Pay only when you need access."
            />
            
            <FeatureCard
              icon={Calendar}
              title="Flexible Duration"
              description="Choose access periods that align with your project timeline."
            />
            
            <FeatureCard
              icon={Download}
              title="Export Anytime"
              description="Export your designs as HTML/CSS code or share via links."
            />
            
            <FeatureCard
              icon={Shield}
              title="Secure Access"
              description="Password-protected sharing and encrypted data storage."
            />
            
            <FeatureCard
              icon={Clock}
              title="No Auto-Renewal"
              description="Passes expire automatically with no recurring charges."
            />
            
            <FeatureCard
              icon={Share2}
              title="Team Sharing"
              description="Create shareable links for team members and stakeholders."
            />
          </div>
        </div>
      </section>

    
{/* Enterprise Plan Section */} 
 <div className="bg-gray-50 py-16"> 
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
    <div className="text-center"> 
      <h2 className="text-3xl font-bold text-gray-900 mb-4"> 
        Supercharge Your Team's Creativity with Enterprise 
      </h2> 
      <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto"> 
        Unlock a new era of collaborative design and accelerated development for your entire organization. Empower every team member to bring their ideas to life, streamline workflows, and build better products, faster. 
      </p> 
      
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-4xl mx-auto"> 
        <div className="grid md:grid-cols-3 gap-8"> 
          <div className="text-center"> 
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4"> 
              <Users className="w-6 h-6 text-blue-600" /> {/* Replaced SVG with Lucide Users icon */}
            </div> 
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Centralized Management</h3> 
            <p className="text-gray-600">A single admin controls team access, seat allocation, and account status, simplifying large-scale deployment and billing.</p> 
          </div> 
          
          <div className="text-center"> 
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4"> 
              <Zap className="w-6 h-6 text-green-600" /> {/* Replaced SVG with Lucide Zap icon */}
            </div> 
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Accelerated Development</h3> 
            <p className="text-gray-600">Export production-ready HTML/CSS and leverage AI for fast, efficient conversion, streamlining your design-to-development workflow.</p> 
          </div> 
          
          <div className="text-center"> 
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4"> 
              <Share2 className="w-6 h-6 text-purple-600" /> {/* Replaced SVG with Lucide Share2 icon */}
            </div> 
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Seamless Collaboration</h3> 
            <p className="text-gray-600">Share designs securely within your organization, enabling teammates to work with shared designs for revision and iteration.</p> 
          </div> 
        </div> 
        
        <div className="mt-8 text-center"> 
          <Link  
            to="/enterprise-guide"  
           className="inline-block bg-black text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
          > 
            Explore Enterprise Features 
          </Link> 
        </div> 
      </div> 
    </div> 
  </div> 
 </div>
      
      {/* FAQ Section - Updated to include rate limit information */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-3">How does pay-as-you-go pricing work?</h3>
              <p className="text-gray-600">
                You purchase access for a specific time period (30, 60, 90, 180, or 360 days). During this period, you have unlimited access to all features. When your access period ends, it simply expires—no automatic renewals or recurring charges.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-3">What happens to my projects when my access expires?</h3>
              <p className="text-gray-600">
                Your projects remain securely stored in your account. You can still view all your existing projects and their details, but you won't be able to create new views or revise existing ones until you purchase a new access pass.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-3">Are there any usage guidelines I should be aware of?</h3>
              <p className="text-gray-600">
              To ensure a smooth experience for everyone, we maintain a fair usage policy that includes some rate limits during periods of high activity. These limits are designed to prevent system overload while allowing normal design workflows to proceed uninterrupted. Most users never encounter these limits during regular usage.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-3">Do I get to keep my free trial generations?</h3>
              <p className="text-gray-600">
                Yes! We offer 10 free generations for new users to try our platform before committing to a purchase. These free generations never expire, so you can use them at your own pace.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Start Creating?</h2>
          <p className="text-xl mb-8">
            Begin with 10 free generations. No credit card required.
          </p>
          <Link to="/signup" className="px-8 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors inline-block">
            Start Free Trial
          </Link>
        </div>
      </section>
      
      {/* Footer */}
      <MainFooter />
    </div>
  );
};

export default Pricing;